import type { PlanDTO } from './plan.dto';
import type { PlanInterval, ProductPlan } from '../types';

export interface PlanMapperOptions {
  /** When true, prefer `*_local` pricing/currency over the base USD values. */
  multiCurrency?: boolean;
}

/**
 * Adapter that converts the API `PlanDTO` into the domain `ProductPlan`.
 * Single responsibility: shape translation + pricing/currency resolution.
 */
export class PlanMapper {
  toDomain(dto: PlanDTO, options: PlanMapperOptions = {}): ProductPlan {
    const { multiCurrency = false } = options;

    const useLocal = multiCurrency && dto.sum_local?.value !== undefined;
    const currency = useLocal
      ? (dto.sum_local as { currency: string }).currency
      : dto.currency || 'USD';

    const recurringPrice = useLocal ? (dto.sum_local as { value: number }).value : dto.sum;

    const trialPrice =
      multiCurrency && dto.trial_sum_local?.value !== undefined
        ? dto.trial_sum_local.value
        : dto.trial_sum;

    const hasTrial = trialPrice !== undefined && trialPrice !== recurringPrice;

    return {
      id: dto.id,
      name: dto.name,
      fullName: dto.name_full,
      price: trialPrice ?? recurringPrice,
      recurringPrice: hasTrial ? recurringPrice : undefined,
      previousPrice: dto.prev_price,
      currency,
      interval: this.toInterval(dto.length),
      lengthCode: dto.length,
      trialLengthCode: hasTrial ? this.resolveTrialCode(dto) : undefined,
      discountPercent: dto.discount_percent,
      priceId: dto.id,
      paypalPlanId: dto.paypalPlanId,
      oneTime: dto.one_time,
    };
  }

  toDomainList(dtos: PlanDTO[], options?: PlanMapperOptions): ProductPlan[] {
    return dtos.map((dto) => this.toDomain(dto, options));
  }

  /**
   * Trial period code. Prefer the explicit `initial_length` field when present;
   * otherwise fall back to parsing the first `<number><d|w|m|y>` segment of the
   * plan id (e.g. `moneyfox_3d299_7d1999` → `3d`).
   */
  private resolveTrialCode(dto: PlanDTO): string | undefined {
    const initial = dto.initial_length?.trim();
    if (initial) return initial.toLowerCase();
    const match = dto.id.match(/(\d+)\s*([dwmy])/i);
    return match ? `${match[1]}${match[2].toLowerCase()}` : undefined;
  }

  private toInterval(length: string): PlanInterval {
    const code = length.toLowerCase().trim();
    if (code === 'lifetime') return 'lifetime';
    if (code.includes('otp')) return 'one_time';

    const value = Number(code.match(/-?\d+/)?.[0] ?? 0);
    if (/y/.test(code) || (/m/.test(code) && value >= 12)) return 'yearly';
    if (/m/.test(code)) return 'monthly';
    if (/w/.test(code) || (/d/.test(code) && value >= 7)) return 'weekly';
    if (/d/.test(code)) return 'daily';
    return 'one_time';
  }
}

export const planMapper = new PlanMapper();
